import { useState } from 'react';
import styled from 'styled-components';
import { Dropdown, Menu } from 'antd';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import ArrowDownIcon from '@/assets/mo/ArrowDown.svg?react';
import Adjusters from './Adjusters';

const introTabs = [
  { key: 'news', label: '최신소식' },
  { key: 'faq', label: '자주 묻는 질문' },
  { key: 'about', label: '올받음 소개' },
  { key: 'adjusters', label: '손사 소개' },
];

const menuItems = [
  { key: 'all', label: '전체' },
  { key: 'name', label: '손사' },
  { key: 'detail', label: '내용' },
];

const AdjustersLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname.split('/').pop() || 'adjusters';

  const [searchKeyword, setSearchKeyword] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const isFilled = searchKeyword.trim().length > 0;

  const menu = <Menu items={menuItems} onClick={({ key }) => setSelectedFilter(key)} />;

  const handleSearch = () => {
    if (!isFilled) return;
    navigate(`/intro/${currentPath}?filter=${selectedFilter}&keyword=${searchKeyword}`);
  };

  return (
    <>
      <Outer>
        <Container>
          <Left>
            <Title>{introTabs.find((tab) => tab.key === currentPath)?.label}</Title>
            <Subtitle>올받음 손해사정사를 소개합니다!</Subtitle>
          </Left>
          <Right>
            <Dropdown overlay={menu} trigger={['click']}>
              <DropdownBox>
                <DropdownLabel>
                  {menuItems.find((item) => item.key === selectedFilter)?.label || '전체'}{' '}
                  <ArrowDownIcon />
                </DropdownLabel>
              </DropdownBox>
            </Dropdown>
            <SearchWrapper hasValue={isFilled}>
              <SearchInput
                placeholder="검색어 입력하세요"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </SearchWrapper>
            <SearchButton onClick={handleSearch} disabled={!isFilled}>
              <SearchText disabled={!isFilled}>검색</SearchText>
            </SearchButton>
          </Right>
        </Container>
      </Outer>

      <SideOuter>
        <SideContainer>
          <SideWrapper>
            <TabList>
              {introTabs.map((tab) => (
                <StyledTab key={tab.key} to={`/intro/${tab.key}`}>
                  {tab.label}
                </StyledTab>
              ))}
            </TabList>
          </SideWrapper>
          <ContentArea>
            <Adjusters />
          </ContentArea>
        </SideContainer>
      </SideOuter>
    </>
  );
};

export default AdjustersLayout;

// ---------------- styled-components ----------------

const Outer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 80px 0 0 0;
`;

const Container = styled.div`
  display: flex;
  width: 100%;
  max-width: 1230px;
  padding: 0 0 80px 0;
  height: 225px;
  justify-content: space-between;
  align-items: flex-end;
  border-bottom: 1px solid #bdbdbd;
`;

const Left = styled.div`
  display: flex;
  width: 276px;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
`;

const Title = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: var(--gray-ui_gray02, #252525);
`;

const Subtitle = styled.div`
  font-size: 18px;
  font-weight: 400;
  color: var(--gray-ui_gray02, #252525);
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DropdownBox = styled.div`
  display: flex;
  width: 121px;
  height: 48px;
  padding: 16px 12px;
  justify-content: space-between;
  align-items: center;
  border-radius: 8px;
  border: 1px solid var(--table-line-table_line01, #bdbdbd);
  background: var(--Primary-white, #fff);
  cursor: pointer;
`;

const DropdownLabel = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
  color: var(--gray-ui_gray04, #6f6f6f);
  font-size: 16px;
`;

const SearchWrapper = styled.div<{ hasValue: boolean }>`
  display: flex;
  width: 221px;
  height: 48px;
  padding: 4px 10px;
  align-items: center;
  border-radius: 8px;
  border: 1px solid var(--table-line-table_line01, #bdbdbd);
  background: var(--Primary-white, #fff);
  border: 1px solid ${({ hasValue }) => (hasValue ? '#0B46DD' : '#BDBDBD')};
`;

const SearchInput = styled.input`
  flex: 1;
  height: 100%;
  border: none;
  padding: 0 10px;
  font-size: 16px;
  outline: none;
  color: #000c24;
`;

const SearchButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  height: 48px;
  padding: 0 20px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  border: none;
  background: ${({ disabled }) => (disabled ? 'var(--Primary-lightgray, #f3f3f3)' : '#0B46DD')};
  color: #fff;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
`;

const SearchText = styled.span<{ disabled?: boolean }>`
  display: flex;
  padding: 0 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  color: ${({ disabled }) => (disabled ? '#6F6F6F' : '#FFF')};
`;

const SideOuter = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
`;

const SideContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 1230px;
  gap: 40px;
`;

const SideWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 30%;
  padding-top: 50px;
  flex-shrink: 0;
`;

const TabList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: start;
`;

const StyledTab = styled(NavLink)`
  padding: 16px 12px;
  font-size: 20px;
  font-weight: 500;
  color: var(--gray-ui_gray04, #6f6f6f);
  text-decoration: none;

  &.active {
    color: #0b46dd;
  }
`;

const ContentArea = styled.div`
  flex: 1;
  padding-top: 50px;
`;
